import React, { useState } from 'react';
import { clsx } from 'clsx';
import { PhotoIcon, EyeIcon } from '@heroicons/react/24/outline';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallback?: React.ReactNode;
  preview?: boolean;
  placeholder?: React.ReactNode;
  width?: string | number;
  height?: string | number;
}

const Image: React.FC<ImageProps> = ({
  src,
  alt,
  fallback,
  preview = false,
  placeholder,
  width,
  height,
  className,
  onError,
  ...props
}) => {
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setIsError(true);
    setIsLoading(false);
    onError?.(e);
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  const openPreview = () => {
    if (preview && !isError) {
      setShowPreview(true);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  // Default placeholder
  const defaultPlaceholder = (
    <div className="flex items-center justify-center bg-gray-100 text-gray-400">
      <PhotoIcon className="h-8 w-8" />
    </div>
  );

  // Default fallback
  const defaultFallback = (
    <div className="flex flex-col items-center justify-center bg-gray-100 text-gray-400 p-4">
      <PhotoIcon className="h-8 w-8 mb-2" />
      <span className="text-sm">Failed to load</span>
    </div>
  );

  const containerStyle = {
    width: width,
    height: height
  };

  return (
    <>
      <div 
        className={clsx('relative overflow-hidden', className)}
        style={containerStyle}
      >
        {/* Loading placeholder */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            {placeholder || defaultPlaceholder}
          </div>
        )}

        {/* Error fallback */}
        {isError ? (
          <div className="w-full h-full">
            {fallback || defaultFallback}
          </div>
        ) : (
          <>
            <img
              src={src}
              alt={alt}
              className={clsx(
                'w-full h-full object-cover transition-opacity',
                isLoading ? 'opacity-0' : 'opacity-100',
                preview && 'cursor-pointer'
              )}
              onError={handleError}
              onLoad={handleLoad}
              onClick={openPreview}
              {...props}
            />
            
            {/* Preview overlay */}
            {preview && !isError && !isLoading && (
              <div 
                className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-all cursor-pointer"
                onClick={openPreview}
              >
                <EyeIcon className="h-8 w-8 text-white" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4"
          onClick={closePreview}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={closePreview}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Image; 